export default function NewOrleansTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for New Orleans Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how New Orleans travelers actually move helps us anticipate the passport urgencies that arise here. From Caribbean cruise departures to oil industry assignments to study-abroad deadlines, Southeast Louisiana travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSY — Louis Armstrong New Orleans International Airport</h3>
                <p className="text-gray-400 text-xs">Kenner, about 15 miles west of downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              MSY is the primary commercial airport for Southeast Louisiana travelers. It offers direct international flights to London, Frankfurt, and Toronto, plus extensive domestic connections to Atlanta, Houston, Dallas, and Charlotte — the major international hubs for New Orleans residents. The airport serves a diverse mix of leisure travelers, business executives, oil and gas workers, and students connecting to overseas destinations.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking MSY international connections. Many countries require six months validity beyond your return date — and cruise lines often require six months validity even for closed-loop sailings.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Port of New Orleans &amp; Cruise Departures</h3>
                <p className="text-gray-400 text-xs">Mississippi River cruise terminal</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of New Orleans is one of the busiest cruise ports in the United States, serving Carnival, Norwegian, Royal Caribbean, and Disney cruise lines with Caribbean, Mexican, and Central American itineraries. New Orleans-area families, retirees in Metairie, and French Quarter business owners book cruise vacations with relatively short lead times — then discover a child's passport expired or a spouse never obtained one. Repositioning cruises and last-minute cruise deals create compressed booking-to-sailing timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Even Caribbean cruises may require passports for all passengers. Some cruise lines accept birth certificates for closed-loop sailings, but any international flight connection or non-U.S. port requires a passport. Verify before booking.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">College &amp; Study Abroad Seasons</h3>
                <p className="text-gray-400 text-xs">Tulane, Loyola, Xavier, UNO</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              New Orleans-area universities send hundreds of students abroad each year — summer programs in Spain, semester exchanges in Germany, research trips to Costa Rica, and public health programs in Africa. Application deadlines for study-abroad programs often fall in October and February, but many students do not think about their passport until after acceptance. Standard post office processing frequently exceeds program deadlines, making expedited service essential.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many college students) require an in-person acceptance clerk visit. We guide Tulane and Loyola students through the exact location, forms, and timeline so they do not miss their program orientation.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Oil &amp; Gas Industry Travel</h3>
                <p className="text-gray-400 text-xs">Offshore and international assignments</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              New Orleans is a major hub for the Gulf Coast oil and gas industry, with companies sending engineers, geologists, and executives to West Africa, the Middle East, and Latin America for drilling, pipeline inspection, and vendor negotiations. These assignments often have fixed start dates and compressed timelines — two weeks notice is not uncommon. Standard passport processing is simply not viable for these professional travel demands.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many countries require work visas in addition to valid passports. Check both your passport expiration and visa requirements as soon as your assignment is confirmed — do not wait until the week before your flight to MSY.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-music-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Jazz &amp; Cultural Festival Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              New Orleans musicians and cultural performers regularly travel to international jazz festivals in Europe, Africa, and Asia. Festival invitations often arrive with 3–6 weeks of lead time, and visa requirements can add additional complexity. We accommodate urgent cultural travel timelines with same-day document review and expedited submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}